import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';
import { VehicleAttachment, VehicleAttachmentCreate, VehicleAttachmentUpdate } from '../models/VehicleAttachment';

/** Metadados enviados no upload para o blob (rotulagem) e persistidos no registro do anexo. */
export interface VehicleAttachmentUploadMeta {
  attachmentType?: string;
  description?: string;
  isPublic?: boolean;
  tags?: string;
  /** Tipo de documento (ex.: identificacao, venda_veicular) */
  documentType?: string;
  /** Tag curta (ex.: CRLV) */
  tag?: string;
  /** Prefixo / placa para rótulo (ex.: ABC1D23) */
  platePrefix?: string;
}

/** Resposta paginada de GET /vehicles/{id}/attachments */
export interface VehicleAttachmentsPagedResponse {
  items: VehicleAttachment[];
  page?: number;
  pageSize?: number;
  totalItems?: number;
  totalPages?: number;
}

function normalizeAttachmentsListResponse(
  res: VehicleAttachment[] | VehicleAttachmentsPagedResponse | unknown
): VehicleAttachment[] {
  if (Array.isArray(res)) {
    return res;
  }
  if (res && typeof res === 'object' && 'items' in (res as object)) {
    const items = (res as VehicleAttachmentsPagedResponse).items;
    return Array.isArray(items) ? items : [];
  }
  return [];
}

type BlobUploadResponse = {
  success: boolean;
  message?: string;
  url: string;
  fileName: string;
  container?: string;
  tags?: {
    tag?: string;
    vehicleId?: string;
    documentType?: string;
    prefix?: string;
  };
};

@Injectable({ providedIn: 'root' })
export class VehicleAttachmentsService {
  private baseUrl = environment.microservices.vehicles;
  private get blobBaseUrl() { return `${this.baseUrl}/blobvehicle`; }

  constructor(private http: HttpClient, private errorService: ErrorService) {}

  list(vehicleId: number, opts?: { type?: string; fileType?: string; publicOnly?: boolean }): Observable<VehicleAttachment[]> {
    let params = new HttpParams();
    if (opts?.type) params = params.set('type', opts.type);
    if (opts?.fileType) params = params.set('fileType', opts.fileType);
    if (opts?.publicOnly) params = params.set('publicOnly', 'true');
    return this.http
      .get<VehicleAttachment[] | VehicleAttachmentsPagedResponse>(`${this.baseUrl}/vehicles/${vehicleId}/attachments`, { params })
      .pipe(
        map(res => normalizeAttachmentsListResponse(res)),
        catchError(err => { this.errorService.handleHttpError(err, 'Erro ao listar anexos do veículo'); return throwError(() => err); })
      );
  }

  getById(id: number): Observable<VehicleAttachment> {
    return this.http.get<VehicleAttachment>(`${this.baseUrl}/vehicle-attachments/${id}`).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar anexo do veículo'); return throwError(() => err); })
    );
  }

  create(payload: VehicleAttachmentCreate): Observable<VehicleAttachment> {
    return this.http.post<VehicleAttachment>(`${this.baseUrl}/vehicle-attachments`, payload).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao criar anexo do veículo'); return throwError(() => err); })
    );
  }

  update(id: number, payload: VehicleAttachmentUpdate): Observable<VehicleAttachment> {
    return this.http.put<VehicleAttachment>(`${this.baseUrl}/vehicle-attachments/${id}`, payload).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao atualizar anexo do veículo'); return throwError(() => err); })
    );
  }

  remove(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/vehicle-attachments/${id}`).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao remover anexo do veículo'); return throwError(() => err); })
    );
  }

  /** Apaga o blob no storage (útil se o delete do anexo falhar parcial). */
  deleteBlob(fileName: string, prefix?: string): Observable<{ success: boolean; deleted?: boolean }> {
    let params = new HttpParams().set('fileName', fileName);
    if (prefix) params = params.set('prefix', prefix);
    return this.http.delete<{ success: boolean; deleted?: boolean }>(`${this.blobBaseUrl}/delete`, { params }).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao apagar arquivo no storage'); return throwError(() => err); })
    );
  }

  /** Substitui o arquivo do anexo mantendo tipo/tag do registro. */
  replaceAndRegister(attachmentId: number, file: File): Observable<VehicleAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<VehicleAttachment>(`${this.baseUrl}/vehicle-attachments/${attachmentId}/replace`, formData)
      .pipe(
        catchError(err => {
          this.errorService.handleHttpError(err, 'Erro ao substituir anexo do veículo');
          return throwError(() => err);
        })
      );
  }

  // Upload integrado ao Blob Functions (envia tipo/tag/placa para rotular no blob e no registro)
  uploadAndRegister(vehicleId: number, file: File, meta?: VehicleAttachmentUploadMeta): Observable<VehicleAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const headers = new HttpHeaders();

    const blobFolderPrefix = `vehicle_${vehicleId}`;
    const plate = (meta?.platePrefix ?? '').toString().trim().replace(/\s+/g, '');

    // Só o ficheiro no multipart — metadados na query (evita o backend misturar partes do form em `tags`).
    let params = new HttpParams().set('prefix', blobFolderPrefix);
    if (meta?.documentType) {
      params = params.set('documentType', meta.documentType);
    }
    if (meta?.tag) {
      params = params.set('tag', meta.tag);
    }
    params = params.set('vehicleId', String(vehicleId));
    if (plate) {
      params = params.set('platePrefix', plate);
    }

    const uploadUrl = `${this.blobBaseUrl}/upload?${params.toString()}`;

    return this.http.post<BlobUploadResponse>(uploadUrl, formData, { headers }).pipe(
      switchMap(res => {
        if (!res?.success || !res.url) throw new Error('Falha no upload para Blob');
        const tagsPayload =
          res.tags != null
            ? JSON.stringify(res.tags)
            : JSON.stringify({
                tag: meta?.tag,
                vehicleId: String(vehicleId),
                documentType: meta?.documentType,
                prefix: plate || undefined
              });
        const payload: VehicleAttachmentCreate = {
          vehicleId,
          fileName: res.fileName,
          filePath: res.url,
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size,
          attachmentType: meta?.attachmentType ?? meta?.documentType,
          description: meta?.description,
          isPublic: meta?.isPublic,
          uploadedBy: 'system',
          tags: meta?.tags ?? tagsPayload
        };
        return this.create(payload);
      }),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao enviar anexo do veículo'); return throwError(() => err); })
    );
  }

  // Utilidades de Blob
  listBlob(prefix?: string): Observable<string[]> {
    let params = new HttpParams();
    if (prefix) params = params.set('prefix', prefix);
    return this.http.get<string[]>(`${this.blobBaseUrl}/list`, { params }).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao listar arquivos'); return throwError(() => err); })
    );
  }

  downloadBlob(fileName: string, prefix?: string): Observable<Blob> {
    let params = new HttpParams().set('fileName', fileName);
    if (prefix) params = params.set('prefix', prefix);
    return this.http.get(`${this.blobBaseUrl}/download`, { params, responseType: 'blob' }).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao baixar arquivo'); return throwError(() => err); })
    );
  }

  /** Baixa o anexo pelo nome no storage (fileName do registro). */
  downloadAttachment(att: VehicleAttachment): Observable<Blob> {
    const fileName = (att.fileName || '').trim() || this.extractBlobNameFromPath(att.filePath);
    if (!fileName) {
      return throwError(() => new Error('Nome do arquivo não informado'));
    }
    return this.downloadBlob(fileName);
  }

  private extractBlobNameFromPath(filePath?: string): string {
    if (!filePath) return '';
    try {
      if (filePath.includes('://')) {
        return decodeURIComponent(filePath.split('?')[0].split('/').pop() || '');
      }
    } catch {
      /* ignore */
    }
    return filePath.split('/').pop() || filePath;
  }
}




